import * as XLSX from "xlsx-js-style";

const HEADERS = [
  "DNI",
  "Nombre",
  "Email",
  "Nº Socio",
  "Tipo usuario",
  "Método pago",
  "Estado migración",
  "Cuota",
  "Último pago",
  "Próximo pago",
  "Migración a tarjeta",
  "Intentos fallidos",
];

const LAST_COL = HEADERS.length - 1;

const COL_WIDTHS = [
  { wch: 12 },
  { wch: 28 },
  { wch: 36 },
  { wch: 10 },
  { wch: 22 },
  { wch: 12 },
  { wch: 16 },
  { wch: 12 },
  { wch: 18 },
  { wch: 18 },
  { wch: 20 },
  { wch: 14 },
];

const BORDER_GRID = {
  top: { style: "thin", color: { rgb: "BFBFBF" } },
  bottom: { style: "thin", color: { rgb: "BFBFBF" } },
  left: { style: "thin", color: { rgb: "BFBFBF" } },
  right: { style: "thin", color: { rgb: "BFBFBF" } },
};

function str(v) {
  if (v == null || v === undefined) return "";
  return String(v).trim();
}

function formatDateCell(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return str(value);
  return d.toLocaleString("es-ES", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function metodoLabel(m) {
  return m === "tarjeta" ? "Tarjeta" : "IBAN";
}

function estadoLabel(e) {
  const map = {
    pendiente: "Pendiente",
    completada: "Completada",
    rechazada: "Rechazada",
  };
  return map[e] || str(e) || "";
}

function cuotaLabel(c) {
  if (!c) return "";
  if (c === "anual") return "Anual";
  if (c === "semestral") return "Semestral";
  return str(c);
}

/** Filas visibles según el texto de búsqueda (misma idea que la tabla con Toolkit). */
export function filterMigrationRowsForExport(rows, searchText) {
  if (!Array.isArray(rows)) return [];
  const q = str(searchText).toLowerCase();
  if (!q) return [...rows];

  const cellStrings = (row) => [
    str(row.dni),
    str(row.name),
    str(row.email),
    row.nAso != null && row.nAso !== "" ? String(row.nAso) : "",
    str(row.tipoUsuario),
    str(row.metodoPago),
    metodoLabel(row.metodoPago),
    str(row.estadoMigracion),
    estadoLabel(row.estadoMigracion),
    str(row.tipoCuota),
    cuotaLabel(row.tipoCuota),
    formatDateCell(row.ultimoPago),
    formatDateCell(row.proximoPago),
    formatDateCell(row.fechaMigracion),
    String(row.intentosFallidos ?? ""),
  ];

  return rows.filter((row) =>
    cellStrings(row).some((s) => s.toLowerCase().includes(q)),
  );
}

function rowToArray(row) {
  return [
    str(row.dni),
    str(row.name),
    str(row.email),
    row.nAso != null && row.nAso !== "" ? String(row.nAso) : "",
    str(row.tipoUsuario),
    metodoLabel(row.metodoPago),
    estadoLabel(row.estadoMigracion),
    cuotaLabel(row.tipoCuota),
    formatDateCell(row.ultimoPago),
    formatDateCell(row.proximoPago),
    formatDateCell(row.fechaMigracion),
    row.intentosFallidos != null && row.intentosFallidos !== ""
      ? String(row.intentosFallidos)
      : "0",
  ];
}

function dataColumnWrapText(col) {
  return [1, 2, 4].includes(col);
}

function applySheetVisuals(ws, planRow, headerRow, dataStart, dataEnd) {
  ws["!cols"] = COL_WIDTHS;
  ws["!merges"] = [
    { s: { r: planRow, c: 0 }, e: { r: planRow, c: LAST_COL } },
  ];

  const planStyle = {
    font: { bold: true, sz: 12, color: { rgb: "FFFFFF" } },
    fill: { patternType: "solid", fgColor: { rgb: "E97132" } },
    alignment: { horizontal: "left", vertical: "center", wrapText: true },
    border: BORDER_GRID,
  };

  const headerStyle = {
    font: { bold: true, sz: 11, color: { rgb: "1F1F1F" } },
    fill: { patternType: "solid", fgColor: { rgb: "D6DCE5" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: true },
    border: BORDER_GRID,
  };

  const rowHeights = [];
  rowHeights[planRow] = { hpt: 36 };

  const planRef = XLSX.utils.encode_cell({ r: planRow, c: 0 });
  if (ws[planRef]) ws[planRef].s = { ...planStyle };

  for (let c = 0; c <= LAST_COL; c++) {
    const ref = XLSX.utils.encode_cell({ r: headerRow, c });
    if (ws[ref]) ws[ref].s = { ...headerStyle };
  }

  let zebra = false;
  if (dataEnd >= dataStart) {
    for (let r = dataStart; r <= dataEnd; r++) {
      rowHeights[r] = { hpt: 22 };
      for (let c = 0; c <= LAST_COL; c++) {
        const ref = XLSX.utils.encode_cell({ r, c });
        const cell = ws[ref];
        if (!cell) continue;
        const wrap = dataColumnWrapText(c);
        const zebraFill = zebra
          ? { patternType: "solid", fgColor: { rgb: "F5F6F8" } }
          : { patternType: "solid", fgColor: { rgb: "FFFFFF" } };
        cell.s = {
          font: { sz: 11, color: { rgb: "222222" } },
          fill: zebraFill,
          alignment: {
            vertical: "top",
            wrapText: wrap,
            horizontal: c === 3 || c === 7 || c === 11 ? "center" : "left",
          },
          border: BORDER_GRID,
        };
      }
      zebra = !zebra;
    }
  }

  ws["!rows"] = rowHeights;
}

/**
 * Exporta migración de pagos a .xlsx (estilo alineado con exportación de congresos).
 * @param {{ titleLine: string, rows: object[] }} params — `rows` ya filtradas (API + búsqueda).
 */
export function exportMigracionesExcel({ titleLine, rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return false;

  const aoa = [[titleLine], HEADERS, ...rows.map(rowToArray)];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const planRow = 0;
  const headerRow = 1;
  const dataStart = 2;
  const dataEnd = 1 + rows.length;

  applySheetVisuals(ws, planRow, headerRow, dataStart, dataEnd);

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Migración");

  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const dateStr = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
  XLSX.writeFile(wb, `migracion-pagos-${dateStr}.xlsx`);
  return true;
}
